import { useSnackbar } from 'notistack'
import { useIdleTimer } from 'react-idle-timer'
import { useContext, useEffect, useState, Suspense } from 'react'
import { TransitionGroup, CSSTransition } from 'react-transition-group'
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import toast, { Toaster, ToastBar, useToasterStore } from 'react-hot-toast'

import './utils/i18n.utils'
import Home from './layouts/home'
import Account from './layouts/account'
import Pockets from './layouts/pockets'
import { useOnlineStatus } from './hooks'
import CryptoPage from './layouts/crypto'
import Insights from './layouts/insights'
import NotFound from './layouts/not-found'
import Customers from './layouts/customers'
import Login from './layouts/auth-process/login'
import SystemUsers from './layouts/system-users'
import RouteGuard from './containers/route-guard'
import { requestAccessToken } from './api/auth.api'
import { resolveAppAccessToken, canLoadDashboardAppData } from './sso/services/app-access.service'
import BanksAccounts from './layouts/banks-accounts'
import { UserContext } from './contexts/user.context'
import { AuthContext } from './contexts/auth.context'
import { saveConnectionIP } from './api/customers.api'
import DocumentsReady from './layouts/documents-ready'
import DocumentDetails from './layouts/document-details'
import CustomerDetails from './layouts/customer-details'
import PaymentMovements from './layouts/payment-movements'
import RequestedReports from './layouts/requested-reports'
import PaymentsRejected from './layouts/payments-rejected'
import { getCurrencyConversion } from './api/payments.api'
import CutomerCompanies from './layouts/customer-companies'
import PaymentsToReview from './layouts/payments-to-review'
import PaymentsCompleted from './layouts/payments-completed'
import { messageByNotificationType } from './lib/common.lib'
import DocumentsToReview from './layouts/documents-to-review'
import DocumentsCompleted from './layouts/documents-completed'
import { AppStateContext } from './contexts/app-state.context'
import CloseModalIcon from './assets/icons/close-modal-icon.svg'
import TwoFactorAuth from './layouts/auth-process/two-factor-auth'
import ForgotPassword from './layouts/auth-process/forgot-password'
import { useSecureStorage } from './contexts/secure-storage.context'
import BankingPartnerDetails from './layouts/banking-partner-details'
import CustomerCompanyDetails from './layouts/customer-company-details'
import { NotificationsContext } from './contexts/notifications.context'
import CustomerCompanyBatches from './layouts/customer-company-batches'
import PaymentReviewedDetails from './layouts/payment-reviewed-details'
// import InsightsFundingsHistory from './layouts/insights-funding-history'
import PaymentToReviewDetails from './layouts/payment-to-review-details'
import CustomerCompanyPayments from './layouts/customer-company-payments'
import DocumentPaymentsDetails from './layouts/document-payments-details'
import DocumentNoveltiesDetails from './layouts/document-novelties-details'
import PaymentWithAlertsDetails from './layouts/payment-with-alerts-details'
import PaymentsWithAlerts from './layouts/customer-company-payments-with-alerts'
import PaymentPendingRoutingDetails from './layouts/payment-pending-routing-details'
import CustomerCompanyMoneyMovements from './layouts/customer-company-money-movements'
import PaymentsPendingRouting from './layouts/customer-company-pending-payment-routing'
import CustomerCompanyFundingsHistory from './layouts/customer-company-fundings-history'
import CustomerCompanyPaymentsHistory from './layouts/customer-company-payments-history'
import { handleCreateEmptyDatabase, handleDeleteExistingDatabase } from './utils/db/common-methods'
import { DevLoginPage } from './sso/pages/dev-login.page'
import { useSuiteAuth } from './sso/contexts/suite-auth.context'
import { isLocalDevWithoutSso, isSuiteSsoRedirectMode } from './sso/sso-flags'
import { shouldUseSuiteLandingLogout, suiteSignOut } from './sso/services/auth.service'
import { logInfo, logSessionSnapshot, logToken } from './sso/utils/sso-logger'

const App = () => {
  const TOAST_LIMIT = 1
  const navigate = useNavigate()
  const location = useLocation()
  const isOnline = useOnlineStatus()
  const { toasts } = useToasterStore()
  const { enqueueSnackbar } = useSnackbar()
  const { handleClearSecret } = useSecureStorage()
  const { userSession, setUserSessionData } = useContext(UserContext)
  const { isAuth, isSync, removeAuth, updateSyncStatus } = useContext(AuthContext)
  const { status: suiteAuthStatus } = useSuiteAuth()
  const { newNotificationType, showNewNotification, updateNewNotificationStatus } = useContext(NotificationsContext)
  const { setCurrentBanksData, USDDollarRate, updateUSDDollarRate, showProcessingSnackbar, updateBanksBalancesDataLoadedStatus } = useContext(AppStateContext)

  const suiteSessionActive =
    isSuiteSsoRedirectMode() && (suiteAuthStatus === 'authenticated' || suiteAuthStatus === 'hydrating')
  const canAccessApp = Boolean(isAuth) || suiteSessionActive

  const [toastId, setToastId] = useState<number>(0)
  const [userActivityState, setUserActivityState] = useState<string>('idle')

  useEffect(() => {
    logInfo('App — routing mode', {
      isAuth: Boolean(isAuth),
      suiteAuthStatus,
      suiteSessionActive,
      canAccessApp,
      isSuiteSsoRedirectMode: isSuiteSsoRedirectMode(),
      isLocalDevWithoutSso: isLocalDevWithoutSso(),
      pathname: location.pathname,
      route: canAccessApp ? 'protected' : isLocalDevWithoutSso() ? 'legacy-login' : 'sso-waiting-or-redirect'
    })
    logToken('App — isAuth token', isAuth)
  }, [isAuth, suiteAuthStatus, suiteSessionActive, canAccessApp, location.pathname])

  useEffect(() => {
    if (
      !isAuth &&
      (location.pathname.includes('payment-to-review-details') ||
        location.pathname.includes('payment-reviewed-details') ||
        location.pathname.includes('payment-pending-routing-details') ||
        location.pathname.includes('payment-with-alerts-details'))
    ) {
      const paymentIdToRedirect = location.pathname.split('/')[2]
      const customerCompanyIdToRedirect = location.pathname.split('/')[3]

      globalThis.localStorage.setItem('paymentIdToRedirect', paymentIdToRedirect)
      globalThis.localStorage.setItem('customerCompanyIdToRedirect', customerCompanyIdToRedirect)
      globalThis.localStorage.setItem('urlToRedirect', getPaymentUrlToRedirect(location.pathname))
    }

    if (canLoadDashboardAppData(String(userSession?._id), isAuth) && !USDDollarRate) {
      handleGetUSDCurrencyRate()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession, isAuth])

  useEffect(() => {
    const onSuiteDashboardReady = () => {
      if (!USDDollarRate) {
        handleGetUSDCurrencyRate()
      }
    }

    globalThis.addEventListener('suite-dashboard-ready', onSuiteDashboardReady)
    return () => globalThis.removeEventListener('suite-dashboard-ready', onSuiteDashboardReady)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    toasts
      .filter((t) => t.visible)
      .filter((_, i) => i >= TOAST_LIMIT)
      .forEach((t) => toast.remove(t.id))
  }, [toasts])

  useEffect(() => {
    if (showNewNotification && newNotificationType) {
      const newToastId = toastId + 1
      let toastComponentToDisplay = (messageByNotificationType as any)[newNotificationType.split('|')[0]]

      if (toastComponentToDisplay.type === 'link') {
        toastComponentToDisplay = handleGetToastComponent(toastComponentToDisplay.message, newNotificationType.split('|')[1])
      } else {
        toastComponentToDisplay = toastComponentToDisplay.message
      }

      setToastId(newToastId)
      updateNewNotificationStatus?.()
      toast(toastComponentToDisplay, {
        duration: 6000,
        position: 'bottom-center',
        id: `notification-${newToastId}`,
        className: 'notifications-container'
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showNewNotification, newNotificationType])

  useEffect(() => {
    if (showProcessingSnackbar) {
      enqueueSnackbar('Creating report', { persist: true, variant: 'creatingReport', anchorOrigin: { vertical: 'bottom', horizontal: 'right' } })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showProcessingSnackbar])

  useEffect(() => {
    if (!canAccessApp) {
      updateSyncStatus?.(false)

      globalThis.sessionStorage.removeItem('dbTables')
      globalThis.sessionStorage.removeItem('dbVersion')
      globalThis.sessionStorage.removeItem('isLoading')

      try {
        handleDeleteExistingDatabase('adamo-pay')
        handleDeleteExistingDatabase('adamo-pay-v2')
        console.log('Database was deleted with success')
      } catch (error) {
        console.log(error)
        globalThis.location.reload()
      }
    }

    if (isAuth && userActivityState === 'idle') {
      start()
      setUserActivityState('active')
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccessApp, isAuth, userActivityState])

  useEffect(() => {
    if (isAuth && userSession) {
      if (userSession?.subRole === 'compliance-specialist') {
        fetch('https://api.ipify.org?format=json')
          .then((response) => response.json())
          .then((data) => handleSaveConnectionIP(data.ip))
          .catch((error) => console.error('Error fetching IP:', error))
      }
    }

    // Do not call handleCloseSession when !isAuth on initial load — SuiteAuthProvider redirects to SSO landing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuth, userSession])

  const getPaymentUrlToRedirect = (path: string): string => {
    if (path.includes('payment-to-review-details')) return 'payment-to-review-details'
    if (path.includes('payment-pending-routing-details')) return 'payment-pending-routing-details'
    if (path.includes('payment-with-alerts-details')) return 'payment-with-alerts-details'

    return 'payment-reviewed-details'
  }

  const handleGetUSDCurrencyRate = async () => {
    try {
      const accessToken = isSuiteSsoRedirectMode() ? await resolveAppAccessToken() : (await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')).data.accessToken
      const currencyConversion = await getCurrencyConversion(accessToken)

      updateUSDDollarRate?.(Number(currencyConversion.data.currency))
    } catch (error: any) {
      console.log('Error handleGetCurrentCobreBalance', error)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      }
    }
  }

  const handleSaveConnectionIP = async (ipData: string) => {
    try {
      const accessToken = isSuiteSsoRedirectMode() ? await resolveAppAccessToken() : (await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')).data.accessToken
      await saveConnectionIP(ipData, globalThis.navigator.userAgent, accessToken)
    } catch (error: any) {
      console.log('Error handleSaveConnectionIP', error)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      }
    }
  }

  const handleCloseSession = (reason?: string) => {
    logInfo('App.handleCloseSession — start', { reason, isAuthPresent: Boolean(isAuth) })
    logToken('App.handleCloseSession — token before remove', isAuth)
    logSessionSnapshot('App.handleCloseSession before removeAuth')

    removeAuth?.()
    handleClearSecret()
    setCurrentBanksData?.([])
    setUserSessionData?.(null)
    // setUserSessionData?.({ _id: '', role: '', subRole: '', username: '', belongsTo: '', firstName: '', lastName: '', adminOfPockets: [] })
    updateBanksBalancesDataLoadedStatus?.(false)

    if (reason === 'idle') {
      setUserActivityState('idle')
      updateNewNotificationStatus?.(true, 'close-session')
    }

    if (shouldUseSuiteLandingLogout()) {
      logInfo('App.handleCloseSession — using suite landing logout')
      void suiteSignOut()
      return
    }

    logInfo('App.handleCloseSession — legacy logout, navigate to /')
    navigate('/', { replace: true })

    if (reason === 'idle') {
      navigate('/', { replace: true, state: { sessionExpired: true } })
    }
  }

  useEffect(() => {
    if (isSync) {
      handleCreateEmptyDatabase()
        .then(() => {
          updateSyncStatus?.(false)

          console.log('Database created with success')
        })
        .catch((error) => console.error('Error App > useEffect-isSync > handleCreateEmptyDatabase', error.message))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSync])

  const onIdle = () => {
    handleCloseSession('idle')

    try {
      handleDeleteExistingDatabase('adamo-pay')
      handleDeleteExistingDatabase('adamo-pay-v2')
      console.log('Database was deleted with success')
    } catch (error) {
      console.log(error)
      globalThis.location.reload()
    }
  }

  const { start } = useIdleTimer({
    onIdle,
    throttle: 500,
    timeout: 3600000,
    stopOnIdle: true,
    startManually: true
  })

  const handleGetToastComponent = (toastMessage: string, toastLink: string) => {
    return (
      <div
        style={{ cursor: 'pointer' }}
        onClick={() => navigate(`/${toastLink}`)}
      >
        {toastMessage}
      </div>
    )
  }

  return (
    <>
      <div
        className="app-container"
        style={isOnline ? {} : { overflow: 'hidden' }}
      >
        <TransitionGroup>
          <CSSTransition
            key={location.key}
            classNames="fade"
            timeout={50}
          >
            <Suspense fallback={<div>Cargando...</div>}>
              <Routes location={location}>
                {isLocalDevWithoutSso() && (
                  <Route
                    path="/dev/login"
                    element={<DevLoginPage />}
                  />
                )}
                {canAccessApp ? (
                  <Route element={<RouteGuard />}>
                    <Route
                      path="/banking-partner-details/:bankingPartnerBdValue"
                      Component={BankingPartnerDetails} //
                    />
                    <Route
                      path="/requested-reports/:customerCompanyId"
                      Component={RequestedReports} //
                    />
                    <Route
                      path="/customer-company-money-movements/:customerCompanyId"
                      Component={CustomerCompanyMoneyMovements} //
                    />
                    <Route
                      path="/payment-to-review-details/:paymentId/:customerCompanyId"
                      Component={PaymentToReviewDetails} //
                    />
                    <Route
                      path="/beneficiary-details/:customerId/banks-accounts"
                      Component={BanksAccounts} //
                    />
                    <Route
                      path="/beneficiary-details/:customerId"
                      Component={CustomerDetails} //
                    />
                    <Route
                      path="/document-details/:documentId"
                      Component={DocumentDetails} //
                    />
                    <Route
                      path="/document-payments-details/:documentId"
                      Component={DocumentPaymentsDetails} //
                    />
                    <Route
                      path="/document-novelties-details/:documentId"
                      Component={DocumentNoveltiesDetails} //
                    />
                    <Route
                      path="/payment-to-review-details/:paymentId"
                      Component={PaymentToReviewDetails} //
                    />
                    <Route
                      path="/payment-reviewed-details/:paymentId/:customerCompanyId"
                      Component={PaymentReviewedDetails} // Por revisar
                    />
                    <Route
                      path="/customer-company-details/:customerCompanyId"
                      Component={CustomerCompanyDetails} //
                    />
                    <Route
                      path="/customer-company-fundings-history/:customerCompanyId"
                      Component={CustomerCompanyFundingsHistory} //
                    />
                    <Route
                      path="/payments-with-alerts/:customerCompanyId"
                      Component={PaymentsWithAlerts} // Por revisar
                    />
                    <Route
                      path="/payment-with-alerts-details/:paymentId/:customerCompanyId"
                      Component={PaymentWithAlertsDetails} // Por revisar
                    />
                    <Route
                      path="/payments-pending-routing/:customerCompanyId"
                      Component={PaymentsPendingRouting}
                    />
                    <Route
                      path="/payment-pending-routing-details/:paymentId/:customerCompanyId"
                      Component={PaymentPendingRoutingDetails}
                    />
                    <Route
                      path="/customer-company-payments-history/:customerCompanyId"
                      Component={CustomerCompanyPaymentsHistory} //
                    />
                    {/* <Route
                      path="/insight-company-funding-history/:customerCompanyId"
                      Component={InsightsFundingsHistory} // Por revisar
                    /> */}
                    <Route
                      path="/customer-company-batches/:customerCompanyId"
                      Component={CustomerCompanyBatches} //
                    />
                    <Route
                      path="/customer-company-payments/:customerCompanyId"
                      Component={CustomerCompanyPayments} //
                    />
                    <Route
                      path="/"
                      Component={Home} //
                    />
                    <Route
                      path="/payment-movements"
                      Component={PaymentMovements} //
                    />
                    <Route
                      path="/account"
                      Component={Account} //
                    />
                    <Route
                      path="/system-users"
                      Component={SystemUsers} //
                    />
                    <Route
                      path="/customer-companies"
                      Component={CutomerCompanies} //
                    />
                    <Route
                      path="/crypto"
                      Component={CryptoPage} //
                    />
                    <Route
                      path="/insights"
                      Component={Insights} //
                    />
                    <Route
                      path="/beneficiaries"
                      Component={Customers} //
                    />
                    <Route
                      path="/pockets"
                      Component={Pockets} //
                    />
                    <Route
                      path="/documents-to-review"
                      Component={DocumentsToReview} //
                    />
                    <Route
                      path="/documents-ready"
                      Component={DocumentsReady} //
                    />
                    <Route
                      path="/documents-completed"
                      Component={DocumentsCompleted} //
                    />
                    <Route
                      path="/payments-to-review"
                      Component={PaymentsToReview} //
                    />
                    <Route
                      path="/payments-validated"
                      Component={PaymentsCompleted} //
                    />
                    <Route
                      path="/payments-completed"
                      Component={PaymentsCompleted} //
                    />
                    <Route
                      path="/payments-rejected"
                      Component={PaymentsRejected} //
                    />
                    <Route
                      path="/payments-returned"
                      Component={PaymentsRejected} //
                    />
                    <Route
                      path="*"
                      Component={NotFound}
                    />
                  </Route>
                ) : isLocalDevWithoutSso() ? (
                  <>
                    <Route
                      path="/"
                      Component={Login}
                    />
                    <Route
                      path="/activate-user"
                      Component={ForgotPassword}
                    />
                    <Route
                      path="/forgot-password"
                      Component={ForgotPassword}
                    />
                    <Route
                      path="/enable-two-factor-auth"
                      Component={TwoFactorAuth}
                    />
                    <Route
                      path="/check-two-factor-auth"
                      Component={TwoFactorAuth}
                    />
                    <Route
                      path="*"
                      Component={Login}
                    />
                  </>
                ) : null}
              </Routes>
            </Suspense>
          </CSSTransition>
        </TransitionGroup>
      </div>
      <Toaster
        containerStyle={{
          bottom: 0,
          width: '100%',
          inset: '16px 0px 0px'
        }}
      >
        {(t) => (
          <ToastBar
            toast={t}
            style={{
              ...t.style,
              animation: t.visible ? 'custom-enter 0.4s ease-in-out' : 'custom-exit 0.4s ease-out'
            }}
          >
            {({ message }) => (
              <>
                {message}
                {t.type !== 'loading' && (
                  <button
                    className="notification-close-button"
                    onClick={() => toast.dismiss(t.id)}
                  >
                    <img
                      src={CloseModalIcon}
                      alt="close-modal-icon"
                    />
                  </button>
                )}
              </>
            )}
          </ToastBar>
        )}
      </Toaster>
    </>
  )
}

export default App
